import React, { useEffect, useState } from 'react';
import Modal, { ModalButton } from '../../Modal/Modal';
import RadioGroup from '../../common/RadioGroup/RadioGroup';
import styled from 'styled-components';
import { colors } from '../../../utils/utils';

export const CONVERSION_RESULT = {
    NO_CONVERSION: 'no_conversion',
    SHORT_CALL: 'short_call',
    CONVERSION_ONE: 'conversion_one',
    CONVERSION_MULTIPLE: 'conversion_multiple',
    NO_ANSWER: 'no_answer',
    IGNORED: 'ignored',
    OVERWRITTEN: 'overwritten'
};
export type ConversionResult = (typeof CONVERSION_RESULT)[keyof typeof CONVERSION_RESULT];

const MAX_TIME_TO_SET_CONVERSIONS = 59 * 1000;

const SubmitButtonWrapper = styled.div`
    margin-top: 16px;
    display: flex;
    justify-content: center;
`;

interface LocalizationLike {
    getText: (key: string) => string;
}
export interface ConversionPayload {
    conversionResult: ConversionResult;
    userId: string;
    callId: string;
}
interface ConversionModalProps {
    saveConversion: (payload: ConversionPayload) => void;
    onFinish?: () => void;
    userId: number | string;
    callId: string;
    localization: LocalizationLike;
}

function ConversionModal(props: ConversionModalProps) {
    const { userId, callId, onFinish, saveConversion } = props;
    const safeOnFinish = onFinish ?? (() => undefined);

    const [conversionResult, setConversionResult] = useState<string | null>(null);

    const onSubmitModal = () => {
        if (conversionResult) saveConversion({ conversionResult, userId: String(userId), callId });
        safeOnFinish();
    };

    const onCloseModal = () => {
        saveConversion({
            conversionResult: CONVERSION_RESULT.NO_ANSWER,
            userId: String(userId),
            callId
        });
        safeOnFinish();
    };

    useEffect(() => {
        const ignoreTimeout = setTimeout(() => {
            saveConversion({
                conversionResult: CONVERSION_RESULT.IGNORED,
                userId: String(userId),
                callId
            });
            safeOnFinish();
        }, MAX_TIME_TO_SET_CONVERSIONS);

        return () => clearTimeout(ignoreTimeout);
    }, [callId, safeOnFinish, saveConversion, userId]);

    return (
        <Modal title={props.localization.getText('conversion.title')} onClose={onCloseModal}>
            <RadioGroup
                onChange={(value: string) => {
                    setConversionResult(value);
                }}
                options={[
                    {
                        label: props.localization.getText('conversion.result.yesOne'),
                        value: CONVERSION_RESULT.CONVERSION_ONE
                    },
                    {
                        label: props.localization.getText('conversion.result.yesMultiple'),
                        value: CONVERSION_RESULT.CONVERSION_MULTIPLE
                    },
                    {
                        label: props.localization.getText('conversion.result.no'),
                        value: CONVERSION_RESULT.NO_CONVERSION
                    }
                ]}
            />
            <SubmitButtonWrapper>
                <ModalButton onClick={onSubmitModal} $color={colors.green.shamrock} disabled={!conversionResult}>
                    {props.localization.getText('conversion.submitButtonText')}
                </ModalButton>
            </SubmitButtonWrapper>
        </Modal>
    );
}

export default ConversionModal;
